/**
 * Замер прикладного RTT по WebSocket.
 *
 * Сервер сравнивает это значение с TCP RTT из Nginx. Транспортное рукопожатие
 * происходит с ближайшим к вам узлом, а прикладной трафик идёт до конца
 * маршрута — поэтому большая разница между ними выдаёт промежуточный прокси
 * (см. analysis/latency.ts).
 *
 * Берётся медиана, а не среднее: одиночный выброс из-за GC или переключения
 * Wi-Fi не должен портить замер.
 */

const PING_COUNT = 7;
const PING_INTERVAL_MS = 120;
const OPEN_TIMEOUT_MS = 4_000;
const TOTAL_TIMEOUT_MS = 8_000;

export interface WsLatency {
  rttMs: number | null;
  samples: number;
}

export async function measureWsLatency(url: string): Promise<WsLatency> {
  let socket: WebSocket;
  try {
    socket = new WebSocket(url);
  } catch {
    return { rttMs: null, samples: 0 };
  }

  const samples: number[] = [];

  try {
    await withTimeout(waitForOpen(socket), OPEN_TIMEOUT_MS);
    await withTimeout(collectSamples(socket, samples), TOTAL_TIMEOUT_MS);
  } catch {
    // Таймаут или сетевая ошибка: возвращаем то, что успели измерить.
  } finally {
    closeQuietly(socket);
  }

  return { rttMs: median(samples), samples: samples.length };
}

function waitForOpen(socket: WebSocket): Promise<void> {
  return new Promise((resolve, reject) => {
    if (socket.readyState === WebSocket.OPEN) return resolve();
    socket.addEventListener('open', () => resolve(), { once: true });
    socket.addEventListener('error', () => reject(new Error('ws error')), { once: true });
    socket.addEventListener('close', () => reject(new Error('ws closed')), { once: true });
  });
}

/**
 * Отправляет ping'и по одному и ждёт ответа на каждый.
 *
 * Последовательно, а не пачкой: параллельные сообщения делят полосу и завышают
 * RTT, а нам нужно время прохождения, не пропускная способность.
 */
async function collectSamples(socket: WebSocket, samples: number[]): Promise<void> {
  for (let i = 0; i < PING_COUNT; i += 1) {
    if (socket.readyState !== WebSocket.OPEN) return;
    const started = performance.now();
    const ok = await pingOnce(socket, i);
    if (!ok) return;
    samples.push(performance.now() - started);
    if (i < PING_COUNT - 1) await sleep(PING_INTERVAL_MS);
  }
}

/** Один цикл ping-pong. Сервер отвечает тем же seq — см. routes/latency.ts. */
function pingOnce(socket: WebSocket, seq: number): Promise<boolean> {
  return new Promise((resolve) => {
    const onMessage = (event: MessageEvent) => {
      if (typeof event.data !== 'string') return;
      try {
        const parsed: unknown = JSON.parse(event.data);
        const reply = parsed as { type?: string; seq?: number };
        // Чужие сообщения игнорируем: ответ обязан совпасть по seq.
        if (reply.type !== 'pong' || reply.seq !== seq) return;
      } catch {
        return;
      }
      cleanup();
      resolve(true);
    };
    const onFail = () => {
      cleanup();
      resolve(false);
    };
    const cleanup = () => {
      socket.removeEventListener('message', onMessage);
      socket.removeEventListener('error', onFail);
      socket.removeEventListener('close', onFail);
    };

    socket.addEventListener('message', onMessage);
    socket.addEventListener('error', onFail, { once: true });
    socket.addEventListener('close', onFail, { once: true });

    try {
      socket.send(JSON.stringify({ type: 'ping', seq }));
    } catch {
      onFail();
    }
  });
}

function median(values: readonly number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 1) return sorted[middle] ?? null;
  const lower = sorted[middle - 1];
  const upper = sorted[middle];
  if (lower === undefined || upper === undefined) return null;
  return (lower + upper) / 2;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('timeout')), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err: unknown) => {
        clearTimeout(timer);
        reject(err instanceof Error ? err : new Error(String(err)));
      },
    );
  });
}

function closeQuietly(socket: WebSocket): void {
  try {
    socket.close();
  } catch {
    // Уже закрыт — ничего делать не нужно.
  }
}
